'use client';

// Package Imports
import React, { useState } from 'react';
import classNames from 'classnames';

// Third-Party Library Imports
import { useForm, SubmitHandler, FieldErrors, RegisterOptions } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';

// Function Imports
import { logMessage } from '@/lib/functions/logMessage';
import { sendContactUs } from '@/lib/functions/sendContact';

// Component Imports
import Toast from '@/components/toast';
import StylizedButton from '@/components/stylizedButton';

// Style Imports 
import styles from "./contact.module.scss";

// Constants Imports
import { REGEX } from '@/lib/constants/common';

type Inputs = {
  name: string;
  email: string;
  message: string;
  phone: string;
};

// Constants
const LABELS = {
  NAME: 'Name',
  EMAIL: 'Email Address',
  PHONE: 'Phone Number',
  MESSAGE: 'Message',
};

const NAME_ERROR = `Please enter a valid ${LABELS.NAME.toLowerCase()}.`;
const EMAIL_ERROR = `Please enter a valid ${LABELS.EMAIL.toLowerCase()}.`;
const PHONE_ERROR = `Please enter a valid ${LABELS.PHONE.toLowerCase()}.`;
const MESSAGE_ERROR = `Please enter a valid ${LABELS.MESSAGE.toLowerCase()}.`;
const MISSING_FIELDS_ERROR = 'The following required fields are missing: ';
const INVALID_FIELDS_ERROR = 'The following fields are invalid: ';
const SEND_EMAIL_ERROR = `Failed to send. Please try again later.`;
const REQUEST_TIMEOUT_ERROR = 'This request may have timed out. Please try again.';
const REQUEST_TIMEOUT = 60000; // 60 seconds

const Contact: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [sendEmailError, setSendEmailError] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [invalidFields, setInvalidFields] = useState<string[]>([]);
  const [timeoutError, setTimeoutError] = useState(false);

  const {
    register,
    handleSubmit,
    clearErrors,
    formState: { errors },
  } = useForm<Inputs>({
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      message: '',
    },
    mode: 'onSubmit', // Validation to run on submit
  });

  const validateField = (
    fieldName: keyof Inputs,
    label: string,
    errors: FieldErrors<Inputs>,
    missing: string[],
    invalid: string[]
  ) => {
    const error = errors[fieldName];
    if (error?.type === 'required') {
      missing.push(label);
    } else if (error) {
      invalid.push(label);
    }
  };

  const validateFields = (errors: FieldErrors<Inputs>) => {
    const missing: string[] = [];
    const invalid: string[] = [];

    validateField('name', LABELS.NAME, errors, missing, invalid);
    validateField('email', LABELS.EMAIL, errors, missing, invalid);
    validateField('email', LABELS.PHONE, errors, missing, invalid);
    validateField('message', LABELS.MESSAGE, errors, missing, invalid);

    setMissingFields(missing);
    setInvalidFields(invalid);

    return missing.length === 0;
  };

  const onError = (errors: FieldErrors<Inputs>) => {
    validateFields(errors);
  };

  const onSubmit: SubmitHandler<Inputs> = async (formData) => {
    clearErrors();
    setMissingFields([]);
    setInvalidFields([]);
    setSendEmailError(false);
    setLoading(true);
    setTimeoutError(false);

    if (!validateFields(errors)) {
      setLoading(false);
      return;
    }

    const timeoutId = setTimeout(() => {
      setLoading(false);
      setTimeoutError(true);
    }, REQUEST_TIMEOUT);

    const submissionData: Inputs = {
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
    };

    setFormSubmitted(true);
    clearTimeout(timeoutId);

    // Send copy of email to customer
    try {
      const response = await sendContactUs(submissionData, true);

      if (response) {
        setFormSubmitted(true);
        clearTimeout(timeoutId);
      } else {
        setTimeoutError(false);
        setSendEmailError(true);
        logMessage('Send email error:');
      }
    } catch (error) {
      logMessage(`Failed to send email to recipient: ${error}`);
    } finally {
      setLoading(false);
    }

    // Send email to Spec's recipient
    try {
      const response = await sendContactUs(submissionData, false);

      if (response) {
        setFormSubmitted(true);
        clearTimeout(timeoutId);
      } else {
        setTimeoutError(false);
        setSendEmailError(true);
        logMessage('Send email error:');
      }
    } catch (error) {
      logMessage(`Failed to send email to recipient: ${error}`);
    } finally {
      setLoading(false);
    }
  };

  const renderFormField = (
    id: keyof Inputs,
    label: string,
    errorId: string,
    errorMessage: string,
    registerOptions: RegisterOptions<Inputs, keyof Inputs> = {},
    type: string = 'text',
    autocomplete: string
  ) => (
    <div className={`formInput formText ${styles.formField}`} key={id}>
      <label htmlFor={id}>
        {label}
        {registerOptions?.required && <span className={styles.required}>*</span>}
      </label>
      {type === 'textarea' ?
        (
          <textarea
            id={id}
            aria-invalid={errors[id] ? 'true' : 'false'}
            aria-describedby={errors[id] ? errorId : undefined}
            {...register(id, registerOptions)}
            disabled={loading}
            autoComplete={autocomplete} />
        ) : (
          <input
            id={id}
            type={type}
            aria-invalid={errors[id] ? 'true' : 'false'}
            aria-describedby={errors[id] ? errorId : undefined}
            {...register(id, registerOptions)}
            disabled={loading}
            autoComplete={autocomplete} />
        )
      }
      {errors[id] && (
        <span id={errorId} className={classNames(styles.error, styles.fontColor_error)}>
          {errorMessage}
        </span>
      )}
    </div>
  );

  return (
    <div className={styles.contactWrapper}>
      <p>We value our customers and are always ready to assist you with any questions, concerns, or feedback you may have. Feel free to get in touch with us using the contact form below. We will make every effort to respond to your inquiry promptly.</p>

      <div className={styles.contactFormContainer} aria-live='polite'>
        {!formSubmitted ? (
          <>
            <h2>Send Us a Message</h2>
            <p>Fields marked with an <span className={classNames(styles.indicator, styles.fontColor_error)}>*</span> are required.</p>
            <div className={styles.formWrapper}>
              <form onSubmit={handleSubmit(onSubmit, onError)} noValidate className={styles.contactForm}>
                <>
                  {renderFormField(
                    'name',
                    LABELS.NAME,
                    uuidv4(),
                    NAME_ERROR,
                    {
                      required: true,
                      pattern: REGEX.USERNAME,
                    },
                    'text',
                    'full_name'
                  )}
                  {renderFormField(
                    'email',
                    LABELS.EMAIL,
                    uuidv4(),
                    EMAIL_ERROR,
                    {
                      required: true,
                      pattern: {
                        value: REGEX.EMAIL,
                        message: 'Please enter a valid email address.',
                      },
                    },
                    'text',
                    'email'
                  )}
                  {renderFormField(
                    'phone',
                    LABELS.PHONE,
                    uuidv4(),
                    PHONE_ERROR,
                    {
                      required: true,
                      pattern: {
                        value: REGEX.PHONE,
                        message: 'Please enter a valid phone number.',
                      },
                    },
                    'text',
                    'phone'
                  )}
                  {renderFormField(
                    'message',
                    LABELS.MESSAGE,
                    uuidv4(),
                    MESSAGE_ERROR,
                    {
                      required: true,
                      pattern: REGEX.EMAIL_MESSAGE,
                    },
                    'textarea',
                    'off')}
                  <StylizedButton className={styles.submitButton} submitType="submit">
                    Send Message
                  </StylizedButton>
                </>
              </form>
              <div role='alert' aria-atomic='true'>
                {missingFields.length > 0 && (
                  <Toast id={uuidv4()} type='error' label={true} message={`${MISSING_FIELDS_ERROR} <em>${missingFields.join(', ')}</em>`} />
                )}
              </div>
              <div role='alert' aria-atomic='true'>
                {invalidFields.length > 0 && (
                  <Toast id={uuidv4()} type='error' label={true} message={`${INVALID_FIELDS_ERROR} <em>${invalidFields.join(', ')}</em>`} />
                )}
              </div>
              <div role='alert' aria-atomic='true'>
                {sendEmailError && (
                  <Toast id={uuidv4()} type='error' label={true} message={SEND_EMAIL_ERROR} />
                )}
              </div>
              <div role='alert' aria-atomic='true'>
                {timeoutError && (
                  <Toast id={uuidv4()} type='error' label={true} message={REQUEST_TIMEOUT_ERROR} />
                )}
              </div>
            </div>
          </>
        ) : (
          <div className={styles.successMessage}>
            <h2>Thank you for your message.</h2>
            <p>We will get back to you shortly.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Contact;
